import { Mail } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { EmailCodeStartResult, SessionView } from '../../shared/api';
import { APP_NAME } from '../../shared/brand';
import { ineligibleSignInDetails } from '../../shared/signIn';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../api/client';
import { qk, useApplySession } from '../api/hooks';
import { BrandMark } from '../components/BrandMark';
import { sharedToken } from '../lib/openedShareLink';

interface GsiApi {
  initialize(opts: {
    client_id: string;
    nonce?: string;
    callback: (response: { credential?: string }) => void;
    ux_mode?: 'popup' | 'redirect';
    auto_select?: boolean;
    use_fedcm_for_button?: boolean;
  }): void;
  renderButton(el: HTMLElement, opts: Record<string, string | number>): void;
  disableAutoSelect(): void;
}

function gsi(): GsiApi | undefined {
  return (window as unknown as { google?: { accounts?: { id?: GsiApi } } }).google?.accounts?.id;
}

let gsiScript: Promise<void> | null = null;
/** Google Identity Services, loaded only on the sign-in screen. */
function loadGsi(): Promise<void> {
  gsiScript ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      gsiScript = null;
      reject(new Error('Google sign-in could not load'));
    };
    document.head.appendChild(script);
  });
  return gsiScript;
}

/** Stop Google from signing straight back in after "Sign out". */
export function forgetGoogleAutoSelect(): void {
  gsi()?.disableAutoSelect();
}

function isDark(): boolean {
  const pref = document.documentElement.getAttribute('data-theme');
  return pref ? pref === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** GIS options for the sign-in button. Width follows the container, capped at Google's 400px maximum. */
export function googleButtonOptions(dark: boolean, containerWidth: number) {
  return {
    type: 'standard' as const,
    theme: dark ? ('filled_black' as const) : ('outline' as const),
    size: 'large' as const,
    text: 'continue_with' as const,
    shape: 'pill' as const,
    width: Math.min(400, Math.max(0, Math.floor(containerWidth))),
  };
}

function message(err: unknown, fallback: string): string {
  return err instanceof ApiError || err instanceof Error ? err.message : fallback;
}

/**
 * The sign-in card's refusal. `signupPath` comes from `signInEligibility`.
 * It is null while Grams is invite-only. PR #28 sets it to `/pricing` when
 * Stripe memberships are on, and this link appears then.
 */
export function IneligibleNotice({ email, signupPath, id }: { email: string; signupPath: string | null; id?: string }) {
  return (
    <p id={id} className="signin__notice" role="alert">
      <b>{email}</b> isn't invited to {APP_NAME} yet. Ask whoever invited you to check the address, or use an email you've added to your account.
      {signupPath ? (
        <>
          {' '}
          <Link to={signupPath}>See memberships</Link>
        </>
      ) : null}
    </p>
  );
}

function ineligibleOf(err: unknown): { email: string; signupPath: string | null } | null {
  if (!(err instanceof ApiError) || err.code !== 'not_allowed') return null;
  return ineligibleSignInDetails(err.details);
}

function GoogleButton({
  clientId,
  nonce,
  onSession,
  onError,
}: {
  clientId: string;
  nonce: string | undefined;
  onSession: (s: SessionView) => void;
  onError: (err: unknown) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const handlers = useRef({ onSession, onError });
  handlers.current = { onSession, onError };
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let cancelled = false;
    let rendered = '';
    const paint = () => {
      const id = gsi();
      if (cancelled || !id) return;
      const opts = googleButtonOptions(isDark(), el.clientWidth);
      // A zero width means the card hasn't been laid out yet. GIS caps the other end at 400.
      if (opts.width === 0) return;
      const key = `${opts.theme}:${opts.width}`;
      if (key === rendered) return;
      rendered = key;
      el.replaceChildren();
      id.renderButton(el, opts);
    };
    loadGsi()
      .then(() => {
        const id = gsi();
        if (cancelled || !el.isConnected || !id) return;
        id.initialize({
          client_id: clientId,
          nonce,
          ux_mode: 'popup',
          use_fedcm_for_button: true,
          callback: ({ credential }) => {
            if (!credential) return;
            handlers.current.onError(null);
            api<SessionView>('/auth/google', { method: 'POST', body: { credential } })
              .then((s) => handlers.current.onSession(s))
              .catch((err) => handlers.current.onError(err));
          },
        });
        paint();
      })
      .catch(() => !cancelled && setFailed(true));
    // The button doesn't track the container or the theme on its own.
    const ro = new ResizeObserver(() => paint());
    ro.observe(el);
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onTheme = () => {
      rendered = '';
      paint();
    };
    mq.addEventListener('change', onTheme);
    const themes = new MutationObserver(onTheme);
    themes.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => {
      cancelled = true;
      ro.disconnect();
      mq.removeEventListener('change', onTheme);
      themes.disconnect();
    };
  }, [clientId, nonce]);

  if (failed) return <p className="small muted">Google sign-in couldn't load. Check your connection, or use an email code.</p>;
  return <div ref={ref} className="signin__google" />;
}

function EmailCode({ onSession }: { onSession: (s: SessionView) => void }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ineligible, setIneligible] = useState<{ email: string; signupPath: string | null } | null>(null);
  const [wait, setWait] = useState(0);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (wait <= 0) return;
    const id = window.setTimeout(() => setWait((w) => w - 1), 1000);
    return () => window.clearTimeout(id);
  }, [wait]);

  const send = async (to: string) => {
    setBusy(true);
    setError(null);
    setIneligible(null);
    try {
      const result = await api<EmailCodeStartResult>('/auth/email/start', { method: 'POST', body: { email: to } });
      if (!result.sent) {
        // Stay on the email step with the address filled in, so a typo can be fixed.
        setEmail(result.email);
        setSentTo(null);
        setIneligible({ email: result.email, signupPath: result.signupPath });
        return;
      }
      setSentTo(result.email);
      setCode('');
      setWait(60);
      window.setTimeout(() => codeRef.current?.focus(), 0);
    } catch (err) {
      setError(message(err, "The code couldn't be sent. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!sentTo) return;
    setBusy(true);
    setError(null);
    try {
      onSession(await api<SessionView>('/auth/email/verify', { method: 'POST', body: { email: sentTo, code } }));
    } catch (err) {
      setError(message(err, "That code didn't work."));
      setBusy(false);
    }
  };

  if (!sentTo) {
    return (
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (email.trim()) void send(email.trim());
        }}
      >
        <label className="field">
          <span className="field__label">Email</span>
          <input
            className="input"
            type="email"
            autoComplete="email"
            inputMode="email"
            required
            value={email}
            aria-invalid={ineligible ? true : undefined}
            aria-describedby={ineligible ? 'signin-ineligible' : undefined}
            onChange={(e) => {
              setEmail(e.target.value);
              setIneligible(null);
            }}
          />
        </label>
        <button className="btn btn--primary btn--block" disabled={busy || !email.trim()}>
          <Mail size={17} /> Email me a code
        </button>
        {ineligible && <IneligibleNotice id="signin-ineligible" email={ineligible.email} signupPath={ineligible.signupPath} />}
        {error && (
          <p className="field__error" role="alert">
            {error}
          </p>
        )}
      </form>
    );
  }

  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        if (/^\d{6}$/.test(code)) void verify();
      }}
    >
      <p className="small muted signin__sent">
        We sent a 6-digit code to <b>{sentTo}</b>. It expires in 10 minutes.
      </p>
      <label className="field">
        <span className="field__label">Code</span>
        <input
          ref={codeRef}
          className="input num signin__code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        />
      </label>
      <button className="btn btn--primary btn--block" disabled={busy || code.length !== 6}>
        Sign in
      </button>
      {error && (
        <p className="field__error" role="alert">
          {error}
        </p>
      )}
      <div className="row between signin__actions">
        <button
          type="button"
          className="btn btn--quiet btn--sm"
          onClick={() => {
            setSentTo(null);
            setError(null);
          }}
        >
          Use a different email
        </button>
        <button type="button" className="btn btn--quiet btn--sm" disabled={busy || wait > 0} onClick={() => void send(sentTo)}>
          {wait > 0 ? `Send a new code (${wait}s)` : 'Send a new code'}
        </button>
      </div>
    </form>
  );
}

export function SignInPage({ session }: { session: SessionView }) {
  const apply = useApplySession();
  const client = useQueryClient();
  // Opened from a shared recipe link: the recipe shows once they're in.
  const [fromLink] = useState(() => sharedToken() !== null);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [googleIneligible, setGoogleIneligible] = useState<{ email: string; signupPath: string | null } | null>(null);
  const onGoogleError = (err: unknown) => {
    const refused = err ? ineligibleOf(err) : null;
    setGoogleIneligible(refused);
    setGoogleError(refused || !err ? null : message(err, 'Google sign-in failed. Try again.'));
    // Each nonce works once: get a fresh one so the next try can succeed.
    if (err) void client.invalidateQueries({ queryKey: qk.session });
  };
  // Signing out (or "Sign in again", or deleting the account) lands here with a session that has no
  // nonce; only the session check issues one, and Google sign-in fails without it.
  useEffect(() => {
    if (session.google && !session.google.nonce) void client.invalidateQueries({ queryKey: qk.session });
  }, [session.google, client]);

  return (
    <main className="signin">
      <div className="card card--pad stack signin__card">
        <div className="signin__id">
          <BrandMark />
          <div>
            <h1 className="section-title">{APP_NAME}</h1>
            <p className="small muted">{fromLink ? 'Sign in to see the recipe that was shared with you.' : 'Sign in to your food and weight log.'}</p>
            <p className="tiny muted" style={{ marginTop: 2 }}>
              Invite only. Use an invited email, or one you've added to your account.
            </p>
          </div>
        </div>
        {session.google && <GoogleButton clientId={session.google.clientId} nonce={session.google.nonce} onSession={apply} onError={onGoogleError} />}
        {googleIneligible && <IneligibleNotice email={googleIneligible.email} signupPath={googleIneligible.signupPath} />}
        {googleError && (
          <p className="field__error" role="alert">
            {googleError}
          </p>
        )}
        {session.google && session.emailCodes && <div className="signin__or">or</div>}
        {session.emailCodes && <EmailCode onSession={apply} />}
        {!session.google && !session.emailCodes && <p className="small muted">Sign-in isn't set up on this server yet.</p>}
        <p className="mkt-inline-links">
          <Link to="/">Home</Link>
          <Link to="/about">About</Link>
        </p>
      </div>
    </main>
  );
}
